import axios from "axios";
import store from "../store";
import { loginUser, logoutUser } from "../redux/logins";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Path of the endpoint used for the cookie-based silent refresh. The backend
// delivers the refresh token as an HttpOnly, Secure, SameSite=Strict cookie
// scoped to this endpoint, so the browser attaches it automatically when
// `withCredentials` is set. The refresh token is never read or sent by JS.
export const REFRESH_ENDPOINT = "/token/refresh/";
export const LOGOUT_ENDPOINT = "/logout";

// Dedupe concurrent refreshes so parallel 401s trigger a single network call.
let refreshPromise = null;

/**
 * Silently obtains a new access token using the HttpOnly refresh cookie and
 * stores it in application memory (Redux). No token is read from or written to
 * localStorage/sessionStorage. Returns the new access token.
 */
export const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(
        `${BASE_URL}${REFRESH_ENDPOINT}`,
        {},
        { withCredentials: true }
      )
      .then((res) => {
        const token = res?.data?.access ?? res?.data?.token;
        if (!token) {
          throw new Error("No access token returned from refresh endpoint");
        }
        // Keep the existing in-memory userId; only refresh the access token.
        const { userId } = store.getState().loginData;
        store.dispatch(loginUser({ userId, token }));
        return token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

/**
 * Clears all in-memory authentication state and the non-sensitive routing
 * flags. The access token only ever lived in memory; the refresh token is an
 * HttpOnly cookie that only the backend can clear (see `logoutRequest`).
 */
export const clearAuthState = () => {
  store.dispatch(logoutUser());
  localStorage.removeItem("status");
  localStorage.removeItem("userId");
  localStorage.removeItem("patientVisitId");
};

/**
 * Calls the backend logout endpoint with credentials so the HttpOnly
 * refresh-token cookie is cleared server-side.
 */
export const logoutRequest = () =>
  axios.get(`${BASE_URL}${LOGOUT_ENDPOINT}`, { withCredentials: true });
